/**
 * Which calls run direct (Jitsi P2P) and which stay on the bridge (JVB).
 *
 * Robert, 2026-09-28: every call of two people runs direct, interpreted
 * calls included. lib-jitsi-meet does the rest on its own: a call starts on
 * the bridge, moves to the direct connection once it connects, moves back to
 * the bridge when a third person joins, and comes back direct
 * `p2p.backToP2PDelay` seconds after it is two again. A room stays on the
 * bridge only where the bridge does a job the direct connection cannot:
 *
 *   - config: p2p is off in config.js, or a superadmin turned it off without
 *     a deploy (the vrsP2P room switch, vrs-server/src/lib/room-join-flags.ts).
 *   - device_setting: this device's "Disable P2P" setting (native Advanced
 *     settings). It can only turn direct media off, never back on over the
 *     server's switch.
 *   - videomail: a VideoMail recording room. Its other occupant is the Jibri
 *     recorder, which records what the bridge sends it.
 *   - observer: this page is a manager's hidden observation. The observer's
 *     media is receive-only because the bridge enforces it; a direct session
 *     would bypass that.
 *   - demo_pair: the Kuwait demo pair's room (room token context
 *     `kuwait_demo`). Prosody's mod_muc_kuwait_demo_guard ends the trial
 *     inside the media service by closing the room; only the bridge cuts the
 *     media off there, a direct connection would depend on both apps leaving.
 *
 * The rule for hidden peers (the observer, the recorder) is enforced per
 * conference by p2pTransport.ts: lib-jitsi-meet counts a hidden participant
 * as the one other peer, so without it a person left alone with an observer
 * would open a direct session to them.
 *
 * Pure: the decision, the token check and the expected transport, with no
 * redux or lib-jitsi-meet imports, so web, native and the tests share it.
 */
import { isVideoMailRoom } from '../voicemail/recordingRoom';
function isRecord(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
/**
 * Whether a decoded room token belongs to the Kuwait demo pair: the server
 * signs `context.kuwait_demo` into it (vrs-server/src/lib/auth.ts), and only
 * into the demo pair's room tokens.
 */
export function isDemoPairRoomToken(payload) {
    return isRecord(payload) && isRecord(payload.context) && payload.context.kuwait_demo !== undefined
        && payload.context.kuwait_demo !== null;
}
/**
 * Direct media for this conference, or the reason it stays on the bridge.
 *
 * @param {Object} options - What this device knows when it creates the conference.
 * @param {boolean|undefined} options.configured - config.p2p.enabled after the
 * server's switch and any link override.
 * @param {boolean|undefined} options.deviceDisabled - The device's own "Disable P2P" setting.
 * @param {boolean} options.observer - This page is a hidden observation.
 * @param {string|undefined} options.roomName - The room being joined.
 * @param {unknown} options.roomToken - The decoded room token, if there is one.
 * @returns {IP2PDecision}
 */
export function resolveP2PPolicy({ configured, deviceDisabled, observer, roomName, roomToken }) {
    if (deviceDisabled === true) {
        return { enabled: false, reason: 'device_setting' };
    }
    if (configured !== true) {
        return { enabled: false, reason: 'config' };
    }
    if (observer) {
        return { enabled: false, reason: 'observer' };
    }
    if (isVideoMailRoom(roomName)) {
        return { enabled: false, reason: 'videomail' };
    }
    if (isDemoPairRoomToken(roomToken)) {
        return { enabled: false, reason: 'demo_pair' };
    }
    return { enabled: true };
}
/**
 * What the call should be on, for telemetry and the debug overlay:
 * direct for exactly two people with nobody hidden, when this device allows it.
 *
 * @param {Object} options - The room as this device sees it.
 * @param {boolean} options.p2pEnabled - The decision above allowed direct media.
 * @param {number} options.participantCount - Visible people, this device included.
 * @param {number} options.hiddenParticipantCount - Hidden observers and recorders.
 * @returns {string} 'p2p', 'jvb', or 'p2p_disabled' for two people who stay on the bridge.
 */
export function expectedMediaTransport({ hiddenParticipantCount, p2pEnabled, participantCount }) {
    if (participantCount + hiddenParticipantCount !== 2) {
        return 'jvb';
    }
    return p2pEnabled ? 'p2p' : 'p2p_disabled';
}
